import React, { useEffect, useRef, memo, useCallback } from "react";
import { useMedia } from "../../contexts/MediaContext";
import DragScrollContainer from "../common/DragScrollContainer";

const MediaSelector = ({
  label,
  aspectRatio = "9:16",
  maxSize = "50MB",
  type = "image", // "image" または "video"
  fieldName,
  selectedMedia,
  onSelect,
  mode = "single", // "single" または "multiple"
}) => {
  const { openMedia, registerSetter } = useMedia();
  const isImage = type === "image";
  const iconClass = isImage ? "fa-regular fa-image" : "fa-regular fa-video";
  const isMultiple = mode === "multiple";

  // 登録済みかどうかを追跡するref
  const isRegisteredRef = useRef(false);

  // コンポーネントがマウントされたときにセッター関数を登録（一度だけ）
  useEffect(() => {
    if (fieldName && onSelect && !isRegisteredRef.current) {
      registerSetter(fieldName, onSelect, isMultiple);
      isRegisteredRef.current = true;
    }

    // コンポーネントのアンマウント時にクリーンアップ
    return () => {
      isRegisteredRef.current = false;
    };
  }, []);

  // MediaGalleryを開く際に選択中のインストラクターを変更する
  const handleOpenMedia = useCallback(() => {
    // fieldNameからインデックスを抽出（例：instructor_10_top_images → 10）
    const match =
      fieldName && fieldName.match(/instructor_(\d+)_(top_images|images)/);
    if (match && match[1]) {
      const instructorIndex = parseInt(match[1], 10);

      // インストラクター選択処理を呼び出す
      if (typeof window.selectInstructor === "function") {
        window.selectInstructor(instructorIndex);
      } else {
        console.warn("window.selectInstructor is not a function");
      }
    }

    // メディアギャラリーを開く
    openMedia(fieldName, selectedMedia, type, mode);
  }, [fieldName, selectedMedia, type, mode, openMedia]);

  // クリック制御付きのメディア開く関数
  const handleOpenMediaWithCheck = useCallback(
    (e) => {
      // 親要素のドラッグスクロールコンテナの状態をチェック
      const dragContainer = e.target.closest(".drag-scroll-container");
      if (dragContainer && dragContainer.dataset.clickDisabled === "true") {
        console.log("MediaSelector click prevented by drag scroll");
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      handleOpenMedia();
    },
    [handleOpenMedia]
  );

  return (
    <div 
      className={`media-selector ${isMultiple ? "multiple" : "single"}`}
      data-field={fieldName}
      data-label={label}
    >
      {isMultiple ? (
        // 複数選択モード
        <div className="img-wrap">
          <DragScrollContainer className="img-item-container">
            <ul className="img-item">
              {/* 追加ボタンは常に表示 */}
              <button
                className={`file ${isImage ? "img" : "movie"} add-button`}
                onClick={handleOpenMediaWithCheck}
              >
                <span className="memo">
                  {isImage ? "Image" : "Movie"}
                  <br />
                  {aspectRatio}
                  {!isImage && (
                    <>
                      <br />
                      max:{maxSize}
                    </>
                  )}
                </span>
                <i className={iconClass}></i>
              </button>
              {selectedMedia &&
                Array.isArray(selectedMedia) &&
                selectedMedia.map((media, index) => (
                  <li
                    key={media.id || index}
                    onClick={handleOpenMediaWithCheck}
                  >
                    {isImage ? (
                      media.image_url ? (
                        <img
                          src={media.image_url}
                          alt={`${label} ${index + 1}`}
                          className="preview"
                          style={{
                            height:
                              media.width > media.height ? "100%" : "auto",
                            width: media.width > media.height ? "auto" : "100%",
                          }}
                          loading="lazy"
                        />
                      ) : (
                        <div className="preview empty-preview">
                          <i className="fa-regular fa-image"></i>
                        </div>
                      )
                    ) : media.image_url ? (
                      <video
                        src={media.image_url}
                        alt={`${label} ${index + 1}`}
                        className="preview"
                        style={{
                          height: media.width > media.height ? "100%" : "auto",
                          width: media.width > media.height ? "auto" : "100%",
                        }}
                      />
                    ) : (
                      <div className="preview empty-preview">
                        <i className="fa-regular fa-video"></i>
                      </div>
                    )}
                  </li>
                ))}
            </ul>
          </DragScrollContainer>
        </div>
      ) : (
        // 単一選択モード：メディアがある場合はプレビュー、ない場合はボタンを表示
        <div className="img-wrap">
          <ul className="img-item">
            {selectedMedia && selectedMedia.image_url ? (
              // メディアが選択されている場合：プレビューを表示
              <li
                onClick={handleOpenMediaWithCheck}
                style={{ cursor: "pointer" }}
              >
                {isImage ? (
                  <img
                    src={selectedMedia.image_url}
                    alt={label}
                    className="preview"
                    style={{
                      height:
                        selectedMedia.width > selectedMedia.height
                          ? "100%"
                          : "auto",
                      width:
                        selectedMedia.width > selectedMedia.height
                          ? "auto"
                          : "100%",
                    }}
                    loading="lazy"
                  />
                ) : (
                  <video
                    src={selectedMedia.image_url}
                    alt={label}
                    className="preview"
                    style={{
                      height:
                        selectedMedia.width > selectedMedia.height
                          ? "100%"
                          : "auto",
                      width:
                        selectedMedia.width > selectedMedia.height
                          ? "auto"
                          : "100%",
                    }}
                  />
                )}
              </li>
            ) : (
              // メディアが選択されていない場合：選択ボタンを表示
              <button
                className={`file ${isImage ? "img" : "movie"}`}
                onClick={handleOpenMediaWithCheck}
              >
                <span className="memo">
                  {isImage ? "Image" : "Movie"}
                  <br />
                  {aspectRatio}
                  {!isImage && (
                    <>
                      <br />
                      max:{maxSize}
                    </>
                  )}
                </span>
                <i className={iconClass}></i>
              </button>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default memo(MediaSelector);
